import { SiteType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import {
  FilterMenuItemLoadingReason,
  FilterMenuOption,
} from '~components/Filters/FilterMenuItem';
import { LiveMapSelectedFilter } from '~constants/enums';
import { useStores } from '~store';
import {
  Filters as LiveMapStoreFilters,
  OrderJobsFilters as LiveMapStoreOrderJobsFilters,
} from '~store/LiveMapStoreNew';

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const ordersFilters: Array<
  Extract<
    keyof LiveMapStoreFilters,
    | 'customerAccountIds'
    | 'departmentIds'
    | 'dispatchNumberIds'
    | 'driverIds'
    | 'dropoffSites'
    | 'pickUpSites'
    | 'projectIds'
    | 'projectsExternalIds'
    | 'requesterIds'
    | 'siteTypes'
    | 'vendorAccountIds'
  >
> = [
  'customerAccountIds',
  'departmentIds',
  'dispatchNumberIds',
  'driverIds',
  'dropoffSites',
  'pickUpSites',
  'projectIds',
  'projectsExternalIds',
  'requesterIds',
  'siteTypes',
  'vendorAccountIds',
];
type OrdersFilters = (typeof ordersFilters)[number];

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const orderJobsFilters: Array<Extract<keyof LiveMapStoreOrderJobsFilters, 'jobStates'>> =
  ['jobStates'];
type OrderJobsFilters = (typeof orderJobsFilters)[number];

type OrdersDispatchFilterKey = OrdersFilters | OrderJobsFilters;

type AppliedFiltersState = Record<
  LiveMapSelectedFilter,
  {
    ordersDispatchFilterKey: OrdersDispatchFilterKey;
    search: string;
  }
>;

export const ordersDispatchFilterKeyBySelectedFilter: Record<
  LiveMapSelectedFilter,
  OrdersDispatchFilterKey
> = {
  [LiveMapSelectedFilter.CUSTOMERS]: 'customerAccountIds',
  [LiveMapSelectedFilter.DEPARTMENTS]: 'departmentIds',
  [LiveMapSelectedFilter.DISPATCH_NUMBERS]: 'dispatchNumberIds',
  [LiveMapSelectedFilter.DRIVERS]: 'driverIds',
  [LiveMapSelectedFilter.DROP_OFF_SITES]: 'dropoffSites',
  [LiveMapSelectedFilter.PICK_UP_SITES]: 'pickUpSites',
  [LiveMapSelectedFilter.PROJECTS]: 'projectIds',
  [LiveMapSelectedFilter.PROJECTS_EXTERNAL_IDS]: 'projectsExternalIds',
  [LiveMapSelectedFilter.REQUESTERS]: 'requesterIds',
  [LiveMapSelectedFilter.SITE_TYPES]: 'siteTypes',
  [LiveMapSelectedFilter.VENDORS]: 'vendorAccountIds',
};

export type DriversRequestCursors = {
  sharedDriversCursor?: string;
  nonSharedDriversCursor?: string;
};

export type TypeaheadedFilterState = {
  cursor?: string | DriversRequestCursors;
  loading: FilterMenuItemLoadingReason | false;
  nonSharedDriversCursor?: string;
  options: FilterMenuOption[];
  searchValue: string;
  sharedDriversCursor?: string;
};

type FiltersAvailableOptionsState = Record<LiveMapSelectedFilter, TypeaheadedFilterState>;

const getAppliedFiltersInInitialState = () => {
  const initialState = Object.entries(ordersDispatchFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as LiveMapSelectedFilter;

      obj[key] = {
        ordersDispatchFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'liveMap_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<LiveMapSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  const initialState = {
    ...Object.values(LiveMapSelectedFilter).reduce((obj, filter) => {
      obj[filter] = {
        loading: false,
        options: parsed[filter] ?? [],
        searchValue: '',
      };

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  initialState.siteTypes = {
    loading: false,
    options: Object.values(SiteType)
      .filter((type) => type !== SiteType.EQUIPMENT_HOME_BASE)
      .map((type) => ({ label: t(`site_types.${type}`), value: `${type}` })),
    searchValue: '',
  };

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, values]) => {
    obj[key as LiveMapSelectedFilter] = values.options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

export default function useLiveMapFilters() {
  const { liveMapStoreNew } = useStores();

  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );
  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const handleLiveMapFiltersReset = useCallback(() => {
    liveMapStoreNew.resetFiltersForCompanyChange();
  }, [liveMapStoreNew]);

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, value]) => {
      obj[key as LiveMapSelectedFilter] = value.options;

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData: setAppliedFilterChipsCollectedData,
    handleLiveMapFiltersReset,
  };
}
